/**
 * Keyboard shortcuts for the three surfaces an operator lives in.
 *
 * The approval queue, the work queue and the inbox are worked as a stack: dozens
 * of items, one decision each, all day. Reaching for the mouse between every one
 * is the whole cost of the job, so `j` and `k` move, and the decisions sit on
 * single letters.
 *
 * This lives under `queue/` because the work queue is the busiest consumer, and
 * the three page directories are owned and reviewed together.
 *
 * WHAT IS BOUND is not decided here. `lib/shortcuts.ts` holds every shortcut as
 * a row, and `useShortcuts` below turns a scope plus a handler per row into the
 * key map `useHotkeys` wants. A screen therefore names the thing it wants to
 * happen and never writes the key, which is what stops a key and the caption
 * under it disagreeing.
 */
import type { ReactNode } from 'react'
import { useEffect, useRef } from 'react'
import { cn } from '../../lib/cn'
import { modLabel } from '../../components/sidebar-model'
import {
  shortcutBindings,
  shortcutById,
  shortcutCaps,
  type ShortcutId,
  type ShortcutIdIn,
  type ShortcutScope,
} from '../../lib/shortcuts'

/**
 * A binding's work. Returning `false` declines the key: it was not this
 * screen's to take this time, so it is left unclaimed for whatever else
 * listens. That is how an open dialog keeps its own Escape when the page under
 * it binds Escape for something that does not apply right now.
 */
type HotkeyHandler = (event: KeyboardEvent) => void | false

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable
}

/**
 * A key pressed inside a panel that has covered the page is not for the page.
 *
 * The command palette got this for free, because focus lands in its search box
 * and a bare letter is already held back while somebody is typing. The
 * shortcuts drawer has no field in it, so without this, reading the list of
 * keys and pressing `j` to see what it did would move the selection in the
 * queue behind the scrim, out of sight.
 *
 * Only bare keys are held back, for the same reason typing holds only bare keys
 * back: `mod+o` has to reach the shell from inside the very panel it closes.
 */
/**
 * Enter on a control belongs to the control.
 *
 * A bare Enter bound at the window would otherwise be prevented before a
 * focused button, link or menu row could act on it, and every control on the
 * screen would stop answering the key a keyboard user presses most. So a bound
 * Enter only fires when focus is on nothing that Enter already means something
 * to.
 */
export function enterBelongsToTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false
  return (
    target.closest('button, a[href], summary, [role="button"], [role="link"], [role="menuitem"], [role="tab"], [role="option"], [role="separator"]') !==
    null
  )
}

function isUnderAModal(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false
  return target.closest('[role="dialog"][aria-modal="true"]') !== null
}

/**
 * Window-level shortcuts, keyed on `event.key` with an optional `mod+` prefix
 * for Cmd or Ctrl.
 *
 * A bare letter never fires while the operator is typing, nor from inside a
 * panel that has covered the page, which is what makes `a` for approve safe to
 * bind next to a textarea and safe to leave bound under an open drawer. Escape
 * and any `mod+` combo do fire in both, deliberately, so an edit can be
 * finished or abandoned without leaving the keyboard.
 *
 * Prefer `useShortcuts` below. This stays exported because it is the half that
 * listens, and a binding with no row in the registry has no caption and no way
 * to be discovered.
 */
export function useHotkeys(
  bindings: Record<string, HotkeyHandler>,
  options: { enabled?: boolean } = {},
): void {
  const enabled = options.enabled ?? true
  const latest = useRef(bindings)
  latest.current = bindings

  useEffect(() => {
    if (!enabled) return undefined
    function onKeyDown(event: KeyboardEvent) {
      const mod = event.metaKey || event.ctrlKey
      if (event.altKey) return
      const handler = mod ? latest.current[`mod+${event.key}`] : latest.current[event.key]
      if (!handler) return
      if (!mod && event.key !== 'Escape' && (isTypingTarget(event.target) || isUnderAModal(event.target))) return
      if (!mod && event.key === 'Enter' && enterBelongsToTarget(event.target)) return
      // Claimed only when the handler takes it. A declined key keeps its
      // default and stays unclaimed, so a dialog's own Escape, which ignores an
      // event somebody already claimed, still closes the dialog.
      if (handler(event) !== false) event.preventDefault()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [enabled])
}

/**
 * The registry, bound.
 *
 * The handler map is keyed by shortcut id and the type demands every id the
 * scope declares, so a row that is listed in the drawer and answered by nobody
 * fails to compile. That is the half of the contract the drawer cannot enforce:
 * the drawer can only draw what is declared, and a declared key that does
 * nothing is the lie this whole arrangement was built to prevent.
 */
export function useShortcuts<S extends ShortcutScope>(
  scope: S,
  handlers: Record<ShortcutIdIn<S>, HotkeyHandler>,
  options: { enabled?: boolean } = {},
): void {
  useHotkeys(shortcutBindings<HotkeyHandler>(scope, handlers), options)
}

/**
 * Cmd or Ctrl, in the spelling of the machine this is running on.
 *
 * `navigator.platform` is deprecated and is still the only thing every browser
 * answers here. It is read at this call site rather than inside `modLabel`,
 * which is kept free of the browser so it can be covered.
 */
export function modKey(): string {
  const platform = typeof navigator === 'undefined' ? undefined : navigator.platform || navigator.userAgent
  return modLabel(platform).trim()
}

/** One key, drawn like a key. */
export function KeyHint({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <kbd
      className={cn(
        'inline-flex min-w-[1.25rem] items-center justify-center rounded border border-line-strong',
        'bg-surface px-1 py-0.5 font-mono text-2xs font-medium text-ink-muted',
        className,
      )}
    >
      {children}
    </kbd>
  )
}

/**
 * A binding's caps, drawn in a row. Shared by the legend and the drawer.
 *
 * `joined` draws the whole chord inside ONE cap with a plus between the keys,
 * which is how the reference prints a shortcut that sits beside the thing it
 * operates rather than in a list of shortcuts. Separate caps are right in the
 * drawer and the legend, where a column of them lines up key by key. Beside a
 * single row they are two small boxes reading as two suggestions, and the plus
 * is what says "and", so the row says one thing.
 */
export function KeyCaps({
  binding,
  joined = false,
  className,
}: {
  binding: string
  joined?: boolean
  className?: string
}) {
  const caps = shortcutCaps(binding, modKey())
  if (joined) return <KeyHint className={cn('shrink-0', className)}>{caps.join('+')}</KeyHint>
  return (
    <span className={cn('inline-flex shrink-0 items-center gap-1', className)}>
      {caps.map((cap) => (
        <KeyHint key={cap}>{cap}</KeyHint>
      ))}
    </span>
  )
}

/**
 * The strip along the bottom of a stack surface, so the shortcuts are learnable
 * without opening anything.
 *
 * It takes ids rather than labels now. It used to take both the keys and the
 * words, which meant every screen that showed a legend was writing the caption
 * a second time, and the drawer would have made it a third.
 */
export function KeyLegend({ ids, className }: { ids: readonly ShortcutId[]; className?: string }) {
  return (
    <ul className={cn('flex flex-wrap items-center gap-x-4 gap-y-1.5 text-2xs text-ink-subtle', className)}>
      {ids.map((id) => {
        const shortcut = shortcutById(id)
        return (
          <li key={id} className="flex items-center gap-1.5">
            <KeyCaps binding={shortcut.binding} />
            <span>{shortcut.title}</span>
          </li>
        )
      })}
    </ul>
  )
}
